import type { SearchClient } from 'algoliasearch';
import algoliasearch from 'algoliasearch';

import { PAGE_CONTEXT } from '@/integrations/algolia/types';
import {
  ALGOLIA_API_KEY,
  ALGOLIA_APP_ID,
  ALGOLIA_OFFLINE_MODE,
  ALGOLIA_PREVIEW_API_KEY,
  ALGOLIA_PREVIEW_SSR_API_KEY,
  ALGOLIA_SSR_API_KEY,
} from '@/lib/const';

import { withApiCallControl } from './withApiCallControl';

type ApiKeyType = 'default' | 'preview' | 'ssr' | 'previewSsr';

export interface SearchClientConfig {
  isFake?: boolean;
  isPreview?: boolean;
  isSsr?: boolean;
  mockContext?: PAGE_CONTEXT;
}

const API_KEYS: Record<ApiKeyType, string> = {
  default: ALGOLIA_API_KEY,
  preview: ALGOLIA_PREVIEW_API_KEY,
  ssr: ALGOLIA_SSR_API_KEY,
  previewSsr: ALGOLIA_PREVIEW_SSR_API_KEY,
} as const;

type MockResponse = {
  facets?: unknown;
  hits: unknown[];
  nbHits: number;
  nbPages: number;
};

const MOCK_DATA_MAP: Record<
  PAGE_CONTEXT,
  () => Promise<{ fakePlpData: MockResponse }>
> = {
  [PAGE_CONTEXT.PLP_WATCHES]: () => import('@/testUtils/fixtures/watches'),
  [PAGE_CONTEXT.PLP_EYEWEAR]: () => import('@/testUtils/fixtures/eyewear'),
  [PAGE_CONTEXT.PLP_STRAPS]: () => import('@/testUtils/fixtures/straps'),
} as const;

const buildMockSearchResult = (mockResponse: MockResponse) => ({
  hits: mockResponse.hits,
  nbHits: mockResponse.nbHits,
  nbPages: mockResponse.nbPages,
  page: 0,
  processingTimeMS: 0,
  hitsPerPage: 20,
  exhaustiveNbHits: false,
  query: '',
  params: '',
  facets: mockResponse.facets,
});

const getMockResponseFromContext = (pageContext: PAGE_CONTEXT) => {
  const mockData = MOCK_DATA_MAP[pageContext];
  return mockData
    ? mockData()
    : Promise.resolve({ fakePlpData: { hits: [], nbHits: 0, nbPages: 0 } });
};

const createFakeClient = (pageContext: PAGE_CONTEXT): SearchClient => {
  const client = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SSR_API_KEY);

  return {
    ...client,
    search: (requests: unknown[]) =>
      getMockResponseFromContext(pageContext).then(
        ({ fakePlpData: mockResponse }) => ({
          results: requests.map(() => buildMockSearchResult(mockResponse)),
        }),
      ),
    initIndex: (indexName: string) => {
      const index = client.initIndex(indexName);
      return {
        ...index,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        search: (query: string, requestOptions: unknown) =>
          getMockResponseFromContext(pageContext).then(
            ({ fakePlpData: mockResponse }) =>
              buildMockSearchResult(mockResponse),
          ),
      };
    },
  } as SearchClient;
};

// To avoid re-creating the client on each call we create the clients once and reuse them
const previewSsrClient = withApiCallControl(
  algoliasearch(ALGOLIA_APP_ID, API_KEYS.previewSsr),
);
const previewClient = withApiCallControl(
  algoliasearch(ALGOLIA_APP_ID, API_KEYS.preview),
);
const ssrClient = withApiCallControl(
  algoliasearch(ALGOLIA_APP_ID, API_KEYS.ssr),
);
const defaultClient = withApiCallControl(
  algoliasearch(ALGOLIA_APP_ID, API_KEYS.default),
);

const resolveClient = (isPreview: boolean, isSsr: boolean): SearchClient => {
  if (isPreview && isSsr) return previewSsrClient;
  if (isPreview) return previewClient;
  if (isSsr) return ssrClient;

  return defaultClient;
};

const createRealClient = (isPreview: boolean, isSsr: boolean): SearchClient => {
  return resolveClient(isPreview, isSsr);
};

export const createSearchClient = (
  config: SearchClientConfig = {},
): SearchClient => {
  const {
    isFake = ALGOLIA_OFFLINE_MODE,
    isPreview = false,
    isSsr = false,
    mockContext,
  } = config;

  if (isFake && mockContext) {
    return createFakeClient(mockContext);
  }

  return createRealClient(isPreview, isSsr);
};
